import React, { useState, useEffect } from 'react';
import { getApiBaseUrl, API_ENDPOINTS } from '../config/api';
import './UnderwritingCoversIssued.css';

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (n) => {
  const num = parseFloat(n);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(num);
};

const getStatusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'paid' || s === 'success') return <span className="badge badge-success">Paid</span>;
  if (s === 'pending') return <span className="badge badge-pending">Pending</span>;
  if (s === 'failed') return <span className="badge badge-failed">Failed</span>;
  return <span className="badge badge-default">{status || '—'}</span>;
};

/**
 * Underwriter: list of covers issued by this user (same columns as "Your Business Transaction" minus Gross Commission, Tax, Net Commission).
 */
const UnderwritingCoversIssued = ({ user, onShowNotification }) => {
  const API_BASE_URL = getApiBaseUrl();
  const [business, setBusiness] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [filterVersion, setFilterVersion] = useState(0);
  const [certDownloadingNumber, setCertDownloadingNumber] = useState(null);

  const downloadCertificate = async (certificateNumber) => {
    if (!certificateNumber || certDownloadingNumber) return;
    setCertDownloadingNumber(certificateNumber);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.getCertificateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ certificateNumber: String(certificateNumber).trim() })
      });
      const result = await res.json().catch(() => ({}));
      if (result.success && result.certificateUrl) {
        const link = document.createElement('a');
        link.href = result.certificateUrl;
        link.download = `Certificate_${certificateNumber}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if (onShowNotification) onShowNotification('Certificate download started.', 'success');
      } else {
        if (onShowNotification) onShowNotification(result.error || 'Could not get certificate download link.', 'error');
      }
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Download failed.', 'error');
    } finally {
      setCertDownloadingNumber(null);
    }
  };

  const fetchCovers = async (pageNum = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
        ...(search.trim() && { search: search.trim() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });
      const res = await fetch(`${API_BASE_URL}/api/admin/underwriting/covers-issued?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load covers');
        setBusiness([]);
        return;
      }
      if (data.success && data.data) {
        setBusiness(data.data.business || []);
        setPagination(data.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
      } else {
        setBusiness([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load covers');
      setBusiness([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCovers(page);
  }, [page, filterVersion]);

  const handleFilter = () => {
    setPage(1);
    setFilterVersion((v) => v + 1);
  };

  return (
    <div className="underwriting-covers-issued">
      <div className="filters-row">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search client, policy…"
          className="filter-input"
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="filter-input"
          placeholder="Start"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="filter-input"
          placeholder="End"
        />
        <button type="button" className="btn-filter" onClick={handleFilter}>Filter</button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner-large" />
          <p>Loading covers…</p>
        </div>
      ) : business.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No covers issued yet</h3>
          <p>Covers you issue (agent or direct business) will appear here.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="covers-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Business</th>
                  <th>Client name</th>
                  <th>Policy Number</th>
                  <th>Cover Type</th>
                  <th>Vehicle Class</th>
                  <th>Premium</th>
                  <th>Payment Status</th>
                  <th>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {business.map((row) => (
                  <tr key={row.id || row.client_id}>
                    <td>{formatDate(row.transaction_date)}</td>
                    <td>{row.agency_name || (row.business_type === 'direct' ? 'Direct' : '—')}</td>
                    <td>{row.insured_name || row.client_id || '—'}</td>
                    <td>{row.policy_number || '—'}</td>
                    <td>{row.cover_type || '—'}</td>
                    <td>{row.vehicle_class || '—'}</td>
                    <td className="amount-cell">{formatCurrency(row.premium_amount)}</td>
                    <td>{getStatusBadge(row.payment_status)}</td>
                    <td>
                      {row.certificate_number ? (
                        <span className="cert-cell-with-download">
                          <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ {row.certificate_number}</span>
                          <button
                            type="button"
                            className="btn-download-cert"
                            disabled={certDownloadingNumber === row.certificate_number}
                            onClick={() => downloadCertificate(row.certificate_number)}
                            title="Download certificate PDF"
                          >
                            {certDownloadingNumber === row.certificate_number ? '…' : 'Download'}
                          </button>
                        </span>
                      ) : (
                        row.payment_status === 'completed' || row.payment_status === 'paid' ? 'Not issued' : 'Pending'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UnderwritingCoversIssued;
